import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import API_ENDPOINTS from "../config/api";

const StatusScreenshotSubmission = () => {
  const navigate = useNavigate();
  const { applicationNumber } = useParams();

  const fileInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null); // { file, preview }
  const [showZoom, setShowZoom] = useState(false);

  // 제출 관련 상태
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // 페이지 로드 시 스크롤 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  const pageVariants = {
    initial: { opacity: 0 },
    in: { opacity: 1, transition: { duration: 0.5 } },
    out: { opacity: 0, transition: { duration: 0.3 } },
  };

  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    in: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.2, staggerChildren: 0.1 },
    },
  };

  const isFormValid = !!uploadedFile;

  // 파일 업로드 핸들러
  const handleUploadAreaClick = () => {
    if (uploadedFile) return;
    fileInputRef.current?.click();
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleOpenDuoLink = () => {
    window.open(
      "https://m.duo.co.kr/personalinfo/",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploadedFile?.preview) URL.revokeObjectURL(uploadedFile.preview);
    setUploadedFile({
      file,
      preview: URL.createObjectURL(file),
    });
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    if (uploadedFile?.preview) URL.revokeObjectURL(uploadedFile.preview);
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // 컴포넌트 언마운트 시 ObjectURL 메모리 정리
  useEffect(() => {
    return () => {
      if (uploadedFile?.preview) URL.revokeObjectURL(uploadedFile.preview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 완료하기: 이미지 업로드(PUT) → sessionStorage 갱신 → /status 이동
  const handleComplete = async () => {
    if (!isFormValid || isUploading) return;
    if (!applicationNumber) {
      setUploadError("신청서 번호를 가져오는데 실패했습니다.");
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile.file);

      // PUT /applications/{applicationNumber}/documents/membership-proof
      const uploadUrl = `${API_ENDPOINTS.BASE_URL}/applications/${applicationNumber}/documents/membership-proof`;
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "스크린샷 업로드에 실패했습니다.");
      }

      // 업로드 성공 후 최신 데이터 조회
      try {
        const storedData = sessionStorage.getItem("applicationData");
        if (storedData) {
          const existingData = JSON.parse(storedData);
          const phoneNumber =
            existingData.phoneNumber || existingData.client?.phoneNumber;
          const accessCode = existingData.accessCode;

          if (phoneNumber && accessCode) {
            const dataResponse = await fetch(
              `${API_ENDPOINTS.BASE_URL}/applications/verify-access`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  phoneNumber,
                  accessCode,
                  lawsuitId: 3,
                }),
              },
            );

            if (dataResponse.ok) {
              const latestData = await dataResponse.json();
              sessionStorage.setItem(
                "applicationData",
                JSON.stringify(latestData),
              );
            }
          }
        }
      } catch (err) {
        console.error("최신 데이터 조회 중 오류:", err);
      }

      navigate("/status", { replace: true });
    } catch (error) {
      setUploadError(error.message || "제출 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <HeroSection>
            <HeroTitle>
              듀오 개인정보 유출 확인
              <br />
              <Strong>'전체 화면'</Strong>을 제출해 주세요.
            </HeroTitle>
            <HeroDescription>
              듀오에서 공식적으로 조회 페이지를 제공합니다.
            </HeroDescription>
          </HeroSection>

          <CaptureButton
            onClick={handleOpenDuoLink}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            유출 확인 페이지 이동하기
          </CaptureButton>

          <ExampleCard>
            <ExampleTitle>개인정보 유출 내역 캡처 예시</ExampleTitle>
            <ExampleSubtitle>
              클릭 시 큰 화면으로 확인할 수 있습니다
            </ExampleSubtitle>
            <ExampleThumb onClick={() => setShowZoom(true)}>
              <ExampleImage
                src="/png/eximg.png"
                alt="개인정보 유출 내역 캡처 예시"
              />
            </ExampleThumb>
          </ExampleCard>

          <UploadCard>
            <UploadCardTitle>유출 확인 화면 제출하기</UploadCardTitle>
            <UploadCardSubtitle>
              유출 확인 결과가 보이도록 캡처하여 제출해주세요
              <br />
              확인 내역이 없다면 먼저 유출 여부를 조회해주세요.
            </UploadCardSubtitle>

            <UploadArea
              onClick={handleUploadAreaClick}
              $hasFile={!!uploadedFile}
            >
              {uploadedFile ? (
                <UploadedPreview>
                  <PreviewImage
                    src={uploadedFile.preview}
                    alt="업로드된 이미지"
                  />
                  <RemoveButton onClick={handleRemoveFile}>
                    <RemoveIcon src="/png/delete.png" alt="삭제" />
                  </RemoveButton>
                </UploadedPreview>
              ) : (
                <UploadPlaceholder>
                  <UploadHint>이곳에 파일을 업로드해주세요</UploadHint>
                </UploadPlaceholder>
              )}
            </UploadArea>
            <HiddenFileInput
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileSelect}
            />

            <HintRow>
              <HintIcon src="/png/iicon2.png" alt="" />
              <HintText>유출 항목이 모두 보이게 캡쳐해 주세요</HintText>
            </HintRow>

            <UploadCardButton
              onClick={handleOpenFilePicker}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              파일 업로드
            </UploadCardButton>
          </UploadCard>

          {uploadError && <ErrorAlert>{uploadError}</ErrorAlert>}

          <CompleteButton
            onClick={handleComplete}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={!isFormValid || isUploading}
          >
            {isUploading ? "제출 중..." : "다음"}
          </CompleteButton>
        </Content>
      </Container>

      <AnimatePresence>
        {showZoom && (
          <ZoomOverlay
            onClick={() => setShowZoom(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ZoomScroll onClick={(e) => e.stopPropagation()}>
              <ZoomCard>
                <ZoomHeader>
                  <ZoomTitle>개인정보 유출 내역 캡처 예시</ZoomTitle>
                  <ZoomClose onClick={() => setShowZoom(false)}>×</ZoomClose>
                </ZoomHeader>
                <ZoomImageWrapper>
                  <ZoomImage src="/png/eximg.png" alt="유출 캡처 예시" />
                </ZoomImageWrapper>
              </ZoomCard>
            </ZoomScroll>
          </ZoomOverlay>
        )}
      </AnimatePresence>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const HeroSection = styled.div`
  width: 100%;
`;

const HeroTitle = styled.h1`
  font-size: 22px;
  font-weight: 500;
  line-height: 1.35;
  color: #333639;
  margin: 0 0 10px 0;
  word-break: keep-all;
`;

const HeroDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #595d63;
  margin: 0;
  word-break: keep-all;
`;

const Strong = styled.span`
  font-weight: 700;
  color: #333639;
`;

const CaptureButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #ffffff;
  color: #e31c20;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #ffe8e8;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const HintRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  margin-bottom: 12px;
`;

const HintIcon = styled.img`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: block;
`;

const HintText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #9091a0;
`;

const ExampleCard = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

const ExampleTitle = styled.h3`
  font-size: 15px;
  font-weight: 500;
  color: #4b5563;
  margin: 0 0 4px 0;
`;

const ExampleSubtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #9091a0;
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const ExampleThumb = styled.button`
  width: 96px;
  height: 96px;
  position: relative;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: white;
  padding: 0;
  overflow: hidden;
  cursor: zoom-in;
  display: block;
  text-align: left;
`;

const ExampleImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const UploadCard = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 56px;
  box-sizing: border-box;
`;

const UploadCardTitle = styled.h3`
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  color: #4b5563;
  font-feature-settings:
    "liga" off,
    "clig" off;
  margin: 0 0 12px 0;
`;

const UploadCardSubtitle = styled.p`
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 125%;
  letter-spacing: -0.14px;
  color: #9091a0;
  margin: 0 0 14px 0;
`;

const UploadCardButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 44px;
  padding: 0 14px;
  margin-top: 12px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  background: #e31c20;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
`;

const ZoomOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background-color: rgba(60, 60, 60, 0.75);
  z-index: 9999;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 24px;
`;

const ZoomScroll = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
`;

const ZoomCard = styled.div`
  width: 100%;
  background-color: #ffffff;
  border-radius: 20px;
  padding: 24px 20px 32px 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
`;

const ZoomHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ZoomTitle = styled.h3`
  flex: 1;
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  color: #4b5563;
  margin: 0;
`;

const ZoomImageWrapper = styled.div`
  width: 100%;
  border: 1px solid #e8ecf4;
  border-radius: 30px;
  padding: 16px;
  box-sizing: border-box;
  background-color: #ffffff;
`;

const ZoomImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
`;

const ZoomClose = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: transparent;
  color: #4b5563;
  font-size: 22px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;
`;

const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f6f8ff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f6f8ff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f6f8ff;

  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const UploadArea = styled.div`
  margin-top: 4px;
  width: 100%;
  min-height: 96px;
  border: 2px dashed ${(props) => (props.$hasFile ? "#E31C20" : "#E8ECF4")};
  border-radius: 12px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$hasFile ? "default" : "pointer")};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
  overflow: hidden;
  padding: 12px;

  &:hover {
    border-color: ${(props) => (props.$hasFile ? "#E31C20" : "#d4dae6")};
  }
`;

const UploadPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px;
`;

const UploadHint = styled.div`
  font-size: 14px;
  color: #9091a0;
  text-align: center;
  line-height: 1.45;
  font-weight: 400;
`;

const UploadedPreview = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 280px;
  object-fit: contain;
  border-radius: 8px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.45);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 2;

  &:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }
`;

const RemoveIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const CompleteButton = styled(motion.button)`
  display: flex;
  min-height: 56px;
  height: 56px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#E0898A" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#DDC2C2" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  margin-top: 40px;

  @media (min-width: 769px) {
    max-width: 470px;
  }
`;

export default StatusScreenshotSubmission;
